<template>
    <teleport to="body">
        <div v-if="props.show"
             class="fixed top-0 bottom-0 left-0 right-0 z-30 overflow-auto bg-black bg-opacity-75"
             role="dialog"
             tabindex="-1"
             @click.self="closeModal">
            <div class="w-10/12 py-20 mx-auto 2xl:w-5/12" @click.stop>
                <div class="bg-white rounded">
                    <div class="p-4">
                        <div class="border w-full bg-white px-4 py-2 rounded relative flex justify-between gap-3 focus-within:ring-2 focus-within:ring-graduan-primary">
                            <input ref="query" @input="runSearch" v-model="searchObj.query"
                                   type="text"
                                   class="text-sm leading-tight flex-grow focus:outline-none"
                                   placeholder="Search">
                            <button type="submit" class="flex-shrink-0">
                                <Search class="w-4 h-4 text-graduan-primary" />
                            </button>
                        </div>

                        <p class="text-sm text-gray-600 mt-1" v-if="!searchIsReady">getting ready...</p>

                        <ul class="mt-4 grid grid-cols-1 gap-1">
                            <li v-for="(result, key) in searchObj.results" :key="result.objectID">
                                <button type="button" @click="goToResult(result.url, result.objectID, key)"
                                        class="block w-full text-left px-4 py-2 bg-white rounded border border-graduan-border hover:border-graduan-primary">
                                    <h2 v-text="result.title" class="text-graduan-heading font-medium text-md"></h2>
                                    <p class="mt-1" v-text="result.summary"></p>
                                    <div class="mt-1 px-1 py-0.5 bg-gray-200 text-gray-600 rounded text-xs inline-block" v-text="result.type"></div>
                                </button>
                            </li>
                        </ul>

                        <p class="mt-4" v-if="searchObj.query === '' && searchObj.results.length === 0">You may search all topics and faqs</p>
                        <p class="mt-4" v-if="searchObj.results.length === 0 && searchObj.query !== ''">No result for "{{ searchObj.query }}". Please refine your search</p>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import axios from '@js/components/axios';
import Search from "@js/icons/Search.vue";
import algoliasearch from 'algoliasearch'
import searchBanWords from "@js/components/forms/searchBanWords";
import searchInsight from "search-insights";
import user from "../user.js";

const props = defineProps({
    show: {
        default: false,
    },
});


const showModal = ref(props.show);

const query = ref('');
const results = ref([]);
const searchClient = ref(null);
const queryId = ref(null);
const passCaptcha = ref(false);
const searchToken = ref(null);
const ip = document.head.querySelector('meta[name="ip"]').content;
const appId = document.head.querySelector('meta[name="algolia-app-id"]').content;
const apiPublic = document.head.querySelector('meta[name="algolia-api-public"]').content;
const searchIndex = document.head.querySelector('meta[name="algolia-search-index"]').content;
const enabledCaptcha = !!document.head.querySelector('meta[name="algolia-search-enabled-captcha"]').content;
const googleRecaptchaKey = document.head.querySelector('meta[name="google-recaptcha-site-key"]').content;

const searchObj = ref({
    init: false,
    show: false,
    searchClient:null,
    query: '',
    queryId: null,
    results: [],
    enabledCaptcha: enabledCaptcha,
    searchToken: null,
    passCaptcha: passCaptcha,
    appId: appId,
    apiPublic: apiPublic,
    searchIndex: searchIndex,
    minQueryCharacterRequired: 3,
    banWords: searchBanWords,
    googleRecaptchaKey: googleRecaptchaKey

});
const getSessionStorageKey = () => {
    return 'searchToken:' + getUserToken();
};

const getUserToken = () => {
    let userToken = ''
    if (user) {
        userToken = 'user-'+user.id
    }
    // else {
    //     userToken = `guest_${this.fingerprint}`
    // }
    return userToken
};

const emit = defineEmits(['close-modal']);
const closeModal = () => {
    showModal.value = false;
    // Emit close-modal event to parent
    emit('close-modal');
};

const fingerprint = computed(() => {
    const userAgent = navigator?.userAgent || '';
    return `userAgent:${userAgent}.ip:${ip}`.trim().length <= 14 ? 'unknown' : `userAgent:${userAgent}.ip:${ip}`;
});

const searchQueryContainBanWords = computed(() => {
    return searchBanWords.some(word => query.value.includes(word));
});

const ready = computed(() => passCaptcha.value && searchClient.value !== null);

const searchIsReady = computed(() => {
    // if (!props.search.passCaptcha) {
    //     console.error('algolia - failed captcha');
    //     return false;
    // }
    // if (!props.search.searchClient) {
    //     console.error('algolia - search client not initiated');
    //     return false;
    // }
    return true;
});

const initSearchClient = () => {
    if (searchObj.value.searchClient) return;

    let client = algoliasearch(
        searchObj.value.appId,
        searchObj.value.apiPublic
    );

    searchInsight('init', {
        appId: searchObj.value.appId,
        apiKey: searchObj.value.searchToken
    });

    searchObj.value.searchClient = client.initIndex(searchObj.value.searchIndex);

    runSearch();
};
const getSearchToken = () => {
    searchObj.value.init = true;

    // if search token is available in session storage, use that key
    const savedSearchToken = sessionStorage.getItem(getSessionStorageKey());
    if (savedSearchToken) {
        console.log('algolia - saved search token available');
        searchObj.value.searchToken = savedSearchToken;
        searchObj.value.passCaptcha = true;
        initSearchClient();
        return;
    }

    if (!props.search.enabledCaptcha) {
        // console.log('bypass captcha');
        axios.post('/web-api/algolia-search-token').then(res => {
            // save search token in session storage
            searchObj.value.searchToken = res.data;
            savedSearchTokenInSessionStorage();
            searchObj.value.passCaptcha = true;
            initSearchClient();
        });
    }

    savedSearchTokenInSessionStorage();
};

const runSearch = () => {
    searchObj.value.show = true;

    initSearchClient()

    if (!searchObj.value.searchClient) {
        searchObj.value.results = [];
        return;
    }

    if (searchObj.value.query.length < searchObj.value.minQueryCharacterRequired) {
        searchObj.value.results = [];
    }

    const requestOptions = {
        hitsPerPage: 5,
        clickAnalytics: true
    };
    if (user) {
        requestOptions.headers = { 'X-Algolia-UserToken': 'user_' + user.id };
    }


    searchObj.value.searchClient
        .search(searchObj.value.query, requestOptions)
        .then(({ hits, queryID }) => {
            searchObj.value.results = hits;
            searchObj.value.queryId = queryID;
            searchObj.value.show = true;
        })
        .catch(err => {
            console.log(err);
            searchObj.value.results = [];
            if (err?.message.includes("parameter expired (less than current date)")) {
                console.log('search token expired, retrieve search token...');
                searchObj.value.searchClient = null;
                sessionStorage.removeItem(getSessionStorageKey());
                getSearchToken();
            }
        });
}

const goToResult = (url, objId, pos) => {
    let userToken = getUserToken()

    searchInsight('clickedObjectIDsAfterSearch', {
        userToken: userToken, // required for Node.js
        eventName: 'Search: '+searchObj.value.searchIndex,
        index: searchObj.value.searchIndex,
        queryID: searchObj.value.queryId,
        objectIDs: [objId],
        positions: [pos+1],
    })

    window.location.href = url
};

const savedSearchTokenInSessionStorage = () => {
    // saved search token in session storage
    // console.log('algolia - saved search token in session storage')
    sessionStorage.setItem(getSessionStorageKey(), searchObj.value.searchToken);
    // after half an hour -> remove search token from session storage
    // disabled this block of code; we've managed to catch exception if search token is expired
    /*setTimeout(() => {
        console.log('algolia - remove search token from session storage')
        sessionStorage.removeItem('searchToken')
    }, 1800000);*/
};

</script>

<style scoped>
/* Your scoped styles */
</style>
